import React, { useEffect, useState } from "react";
import {
  DatePicker,
  Input,
  Select,
  Button,
  Breadcrumb,
  message,
  Modal,
  ConfigProvider,
} from "antd";
import dayjs from "dayjs";
import { DoubleRightOutlined } from "@ant-design/icons";
import { SetHeading } from "../../context/NavbarContext";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  getExpenseTypes,
  createExpenseType,
  createExpenseEntry,
  getExpenseTransactions,
  editOrDeleteExpenseEntry,
  getExpensePaymentMethods,
  fetchMethodBalance,
} from "../../API  Functions/ExpenseManagementFunctions";
import ExpenseTransactionTable from "./ExpenseTransactionTable";
import SelectBox from "../CustomWidgets/SelectBox";
import moment from "moment";
import { toast } from "react-toastify";
import EditExpenseModal from "./EditExpenseModal";
import DateFilter from "../ui/DateFilter";
import { checkPermission } from "../../Utils/permissionHelper";

const { Option } = Select;

const ExpenseTracker = () => {
  const { t, i18n } = useTranslation();
  const userId = useSelector((state) => state?.login?.user?.result?.user_id);
  const companyId = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );

  const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);

  const [expenseDate, setExpenseDate] = useState(null);
  const [expenseType, setExpenseType] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [remarks, setRemarks] = useState("");

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [expensePaymentMethods, setExpensePaymentMethods] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  const [selectedRange, setSelectedRange] = useState("thisMonth");
  const [loading, setloading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [editingExpense, setEditingExpense] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [currentLimit, setCurrentLimit] = useState(50); // Default limit
  const [currentOffset, setCurrentOffset] = useState(0); // Default offset

  const [saving, setSaving] = useState(false);
  const today = dayjs();
  const formatToday = today.format("YYYY-MM-DD");
  const startofMonth = today.startOf("month").format("YYYY-MM-DD");

  const [startDate, setStartDate] = useState(startofMonth);
  const [endDate, setEndDate] = useState(formatToday);
  const [balance, setBalance] = useState(null);

  const allStores = useSelector(
    (state) => state?.storeReducer?.selectedStore?.is_select_all_store,
  );

  // Fetch expense types
  const fetchExpenseTypes = async () => {
    try {
      setLoadingTypes(true);
      const res = await getExpenseTypes(companyId);
      const types = res?.result?.expense_types?.map((type) => type.name) || [];
      setExpenseTypes(types);
      setLoadingTypes(false);
    } catch (err) {
      console.error(err);
      setLoadingTypes(false);
    }
  };

  const fetchExpensePaymentMethods = async () => {
    try {
      setLoadingTypes(true);
      const res = await getExpensePaymentMethods(companyId);
      // const types = res?.result?.expense_types?.map((type) => type.name) || [];
      setExpensePaymentMethods(res?.result?.data);
      setLoadingTypes(false);
    } catch (err) {
      console.error(err);
      setLoadingTypes(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (start_date, end_date, limit, offset) => {
    try {
      const res = await getExpenseTransactions({
        user_id: userId,
        company_id: companyId,
        start_date,
        end_date,
        limit: limit,
        offset: offset,
        is_expense: true,
      });
      if (res?.result?.status === "success") {
        setTransactions(res.result.transactions || []);
      } else {
        message.error(t("Failed to fetch transactions"));
      }
    } catch (err) {
      console.error(err);
      message.error(t("Failed to fetch transsetIsModalVisibleactions"));
    }
  };

  useEffect(() => {
    fetchExpenseTypes();
    fetchExpensePaymentMethods();
    const today = dayjs().format("YYYY-MM-DD");
    fetchTransactions(startDate, endDate, currentLimit, currentOffset);
  }, [currentLimit, currentOffset]);

  const resetForm = () => {
    setExpenseDate(null);
    setExpenseType("");
    setAmount("");
    setPaymentMethod("Cash");
    setRemarks("");
  };

  const fetchBalance = async (selectedType) => {
    if (!selectedType) return;

    try {
      setBalanceLoading(true);
      let payload = {
        store_ids: [companyId],
        from_type: selectedType,
      };

      const res = await fetchMethodBalance(payload);
      if (res?.result?.status_code === 200) {
        const balanceAmt = parseFloat(res?.result?.balance || 0);
        setBalance(balanceAmt);
      } else {
        setBalance(null);
        message.error(t("Failed to fetch balance"));
      }
    } catch (err) {
      console.error(err);
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleSave = async () => {
    if (!expenseDate || !expenseType || !amount) {
      message.warning(t("Please fill all required fields"));
      return;
    }

    if (saving) return; // Prevent multiple clicks

    setSaving(true);

    let typeName = expenseType;

    try {
      // Create new expense type if needed
      if (!expenseTypes.includes(expenseType)) {
        const createdType = await createExpenseType(companyId, expenseType);
        if (!createdType) return; //  This return skips setSaving(false)
        typeName = createdType.result?.expense_types?.[0]?.name || expenseType;
        await fetchExpenseTypes();
      }

      // Create expense entry
      await createExpenseEntry({
        company_id: companyId,
        user_id: userId,
        amount: parseFloat(amount),
        remark: remarks,
        expense_type: typeName,
        expense_date: dayjs(expenseDate).format("YYYY-MM-DD"),
        payment_method: paymentMethod,
      });

      message.success(t("Expense saved successfully"));
      resetForm();
      setSelectedRange("thisMonth");
      setStartDate(startofMonth);
      setEndDate(formatToday);
      fetchTransactions(startofMonth, formatToday, 50, 0);
    } catch (err) {
      console.error(err);
      message.error(t("Failed to save expense"));
    } finally {
      setSaving(false); // Always reset saving, even after errors/returns
    }
  };

  const disabledExpenseDate = (current) => {
    return current && current > dayjs().endOf("day");
  };

  // Check if all fields are empty (for Cancel button)
  const isFormEmpty =
    !expenseDate && !expenseType && !amount && !paymentMethod && !remarks;
  const isCancelDisable = !expenseDate && !expenseType && !amount;
  // Check if required fields are filled (for Save button)
  const isSaveDisabled = !expenseDate || !expenseType || !amount;

  const handleEdit = (record) => {
    console.log("fhrfhesjsdg", record);

    setEditingExpense(record);
    setEditModalOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      const payload = {
        user_id: userId,
        company_id: companyId,
        record_id: record.id,
        is_delete: true,
      };

      const res = await editOrDeleteExpenseEntry(payload);
      // console.log("DDDD", res);

      if (res?.result?.status_code === 200) {
        toast.success(t("Expense deleted successfully"));
        fetchTransactions(startDate, endDate, currentLimit, currentOffset);
      } else {
        message.error("Failed to delete expense");
      }
    } catch (err) {
      message.error("Error deleting expense");
    }
  };

  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setEditModalOpen(true);
  };

  return (
    <div className="flex w-full flex-col overflow-y-auto bg-[#F6F7F9] pb-2 md:h-full">
      <SetHeading value={t("Expense Management")} />
      <div className="m-4 md:m-0 md:mx-5 overflow-y-auto custom-scrollbar">
        {/* Add Expense Form */}
        <div className=" md:my-5 md:me-2">
          <h3 className="font-bold mb-4">{t("Add Expense")}</h3>
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:gap-y-4 mb-4 sm:grid-cols-2 sm:gap-8">
            {/* Expense Date */}
            <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">
                {t("Expense Date")}
              </label>
              <DatePicker
                style={{ width: "100%" }}
                placeholder={t("Select Date")}
                value={expenseDate}
                onChange={(date) => setExpenseDate(date)}
                className="h-10 "
                disabledDate={disabledExpenseDate} // <- Prevent future dates
              />
            </div>

            {/* Expense Type */}
            <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">
                {t("Expense Type")}
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder={t("Select or Create Expense Type")}
                value={t(expenseType)}
                onChange={(value) => setExpenseType(value)}
                onSearch={(value) => setExpenseType(value)}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={
                  loadingTypes ? t("Loading...") : t("Type to create new")
                }
                className="h-10 "
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div className="p-2 border-t flex">
                      <Input
                        placeholder={t("Enter new expense type")}
                        value={expenseType}
                        onChange={(e) => setExpenseType(e.target.value)}
                        className="flex-1 h-8"
                      />
                      <Button
                        type="link"
                        onClick={() =>
                          message.info(
                            t("New expense type will be created on Save"),
                          )
                        }
                      >
                        {t("Create")}
                      </Button>
                    </div>
                  </>
                )}
              >
                {expenseTypes.map((type) => (
                  <Option key={type} value={type}>
                    {t(type)}
                  </Option>
                ))}
              </Select>
            </div>


             <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">
                {t("Payment Method")}
              </label>

              <Select
                style={{ width: "100%" }}
                value={paymentMethod}
                onChange={(value) => {
                  setPaymentMethod(value);
                  fetchBalance(value);
                }}
                className="h-10"
              >
                {expensePaymentMethods?.map((item) => (
                  <Option key={item.name} value={item.name}>
                    {t(item.name)}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Amount */}
            <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">{t("Amount")}</label>
              {/* <Input
                type="number"
                placeholder={t("Enter Amount")}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-10 "
              /> */}
              <Input
                type="text"
                placeholder={t("Enter Amount")}
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow only digits and optional one decimal point (not at start)
                  const regex = /^(?!0\d)(\d+(\.\d{0,2})?)?$/;

                  // ✅ Allow empty value, or valid numeric input
                  if (value === "" || regex.test(value)) {
                    // Prevent leading '.' or '-'
                    if (value.startsWith(".") || value.startsWith("-")) return;
                    setAmount(value);
                  }
                }}
                inputMode="decimal"
                maxLength={10}
                className="h-10"
              />
              {balanceLoading ? (
                <p className="text-sm text-gray-500 mt-1">
                  {t("Fetching balance...")}
                </p>
              ) : balance !== null ? (
                <p
                  className={`text-sm italic mt-1 
                
                  `}
                >
                  {t("Available Balance")}: {balance.toFixed(2)}
                </p>
              ) : null}
            </div>

            {/* Payment Method */}
           

            {/* Remarks */}
            <div className="flex flex-col ">
              <label className="text-xs md:text-sm mb-1">{t("Remarks")}</label>
              <Input.TextArea
                placeholder={t("Enter Remarks")}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className=""
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="mb-6 flex gap-3 flex-row justify-end">
            <button
              onClick={resetForm}
              className={`w-full sm:w-auto px-4 py-2 font-semibold text-sm border rounded-lg
            ${
              isCancelDisable
                ? "bg-white text-primaryBlue border-primaryBlue hover:bg-red-600 opacity-70 cursor-not-allowed"
                : "bg-white text-primaryBlue border-primaryBlue hover:bg-red-600"
            }`}
              disabled={isCancelDisable}
            >
              {t("Cancel")}
            </button>
            <button
              // onClick={handleSave}
              onClick={() => {
                if (checkPermission("/settings/expense", isGinne)) {
                  handleSave();
                }
              }}
              className={`w-full sm:w-auto px-4 py-2 font-semibold text-sm border rounded-lg
    ${
      isSaveDisabled || saving
        ? "bg-primaryBlue text-white border-primaryBlue opacity-70 cursor-not-allowed"
        : "bg-primaryBlue text-white border-primaryBlue hover:bg-red-600"
    }`}
              disabled={isSaveDisabled || saving} // 🔹 Disable when saving
            >
              {saving ? t("Saving...") : t("Save")}
            </button>
          </div>
        </div>

        {/* {transactions?.length < 1 && selectedRange == "thisMonth" ? (
          <></>
        ) : (
          <> */}
        <DateFilter
          setSelectedRange={setSelectedRange}
          selectedRange={selectedRange}
          SubmitFilter={(start, date) => {
            setStartDate(start);
            setEndDate(date);
            fetchTransactions(start, date, 50, 0);
          }}
        />
        <ExpenseTransactionTable
          additionalData={transactions}
          onEdit={handleEdit}
          onDelete={handleDelete}
          pagination={(values) => {
            setCurrentLimit(values.limit);
            setCurrentOffset(values.offset);
          }}
        />
        {/* </>
        )} */}

        <EditExpenseModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          expense={editingExpense}
          onUpdate={() => fetchTransactions(startDate, endDate, 50, 0)} // ✅ Refresh list
        />
      </div>
    </div>
  );
};

export default ExpenseTracker;
